import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Gauge, Calendar, Fuel, Cog, Calculator, Heart, SlidersHorizontal } from "lucide-react";
import { formatBRL, formatKM } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VehicleShowcase = ({ onSimulate, onInterest }) => {
  const [vehicles, setVehicles] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [category, setCategory] = useState("Todos");
  const [brand, setBrand] = useState("Todas");
  const [maxPrice, setMaxPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/vehicles`)
      .then((res) => setVehicles(res.data))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
    axios
      .get(`${API}/config/categorias`)
      .then((res) => setCategorias(res.data.categorias || []))
      .catch(() => {});
  }, []);

  const categories = ["Todos", ...new Set([...categorias, ...vehicles.map((v) => v.categoria).filter(Boolean)])];

  const marcas = [...new Set(vehicles.map((v) => v.marca))].sort();
  const priceMin = vehicles.length ? Math.min(...vehicles.map((v) => v.preco)) : 0;
  const priceMax = vehicles.length ? Math.max(...vehicles.map((v) => v.preco)) : 2500000;
  const effectiveMax = maxPrice ?? priceMax;

  const filtered = vehicles.filter(
    (v) =>
      (category === "Todos" || v.categoria === category) &&
      (brand === "Todas" || v.marca === brand) &&
      v.preco <= effectiveMax
  );

  const hasFilters = category !== "Todos" || brand !== "Todas" || maxPrice !== null;
  const clearFilters = () => {
    setCategory("Todos");
    setBrand("Todas");
    setMaxPrice(null);
  };

  const priceFill = priceMax > priceMin ? ((effectiveMax - priceMin) / (priceMax - priceMin)) * 100 : 100;

  return (
    <section id="estoque" className="py-24 sm:py-32 relative" data-testid="vehicle-showcase">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12"
        >
          <div>
            <p className="text-xs font-mono2 tracking-widest uppercase text-emerald-400 mb-3">01 — Vitrine</p>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white">
              Estoque em <span className="text-emerald-400">tempo real</span>
            </h2>
            <p className="mt-3 text-sm text-slate-400" data-testid="vehicle-count">
              {filtered.length} de {vehicles.length} veículo(s)
            </p>
          </div>

          <div className="flex flex-col gap-3 lg:items-end" data-testid="vehicle-filters">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`filter-${c.toLowerCase()}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${
                    category === c ? "bg-emerald-500 text-emerald-950" : "glass text-slate-300 hover:text-emerald-400"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <SlidersHorizontal className="w-4 h-4 text-emerald-400 hidden sm:block" />
              <select
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                data-testid="filter-brand-select"
                className="glass rounded-full px-4 py-2 text-sm text-slate-200 outline-none cursor-pointer"
              >
                <option value="Todas" className="bg-[#131620]">Todas as marcas</option>
                {marcas.map((m) => (
                  <option key={m} value={m} className="bg-[#131620]">{m}</option>
                ))}
              </select>
              <div className="glass rounded-full px-4 py-2 flex items-center gap-3">
                <span className="text-xs text-slate-400 whitespace-nowrap">Preço até</span>
                <input
                  type="range"
                  min={priceMin}
                  max={priceMax}
                  step="10000"
                  value={effectiveMax}
                  onChange={(e) => setMaxPrice(Number(e.target.value))}
                  style={{ "--fill": `${priceFill}%` }}
                  className="w-32 sm:w-40"
                  data-testid="filter-price-slider"
                />
                <span className="text-sm font-semibold text-emerald-400 whitespace-nowrap" data-testid="filter-price-value">
                  {formatBRL(effectiveMax)}
                </span>
              </div>
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  data-testid="filter-clear"
                  className="text-xs font-semibold text-slate-400 hover:text-emerald-400 underline underline-offset-4 transition-colors"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 rounded-2xl bg-[#131620] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="glass rounded-3xl p-14 text-center" data-testid="vehicle-empty">
            <p className="font-display text-xl font-bold text-white mb-2">Nenhum veículo nesses filtros</p>
            <p className="text-sm text-slate-400 mb-6">Ajuste a faixa de preço ou a marca para ver mais opções.</p>
            <button
              onClick={clearFilters}
              data-testid="filter-clear-empty"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-6 py-3 rounded-full transition-colors"
            >
              Limpar filtros
            </button>
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vehicle-grid">
            <AnimatePresence mode="popLayout">
              {filtered.map((v, i) => (
                <motion.article
                  layout
                  key={v.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="group relative rounded-2xl overflow-hidden bg-[#131620] border border-slate-800/60 hover:border-emerald-500/40 transition-colors duration-500"
                  data-testid={`vehicle-card-${v.id}`}
                >
                  <div
                    className="relative h-56 overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/veiculo/${v.id}`)}
                    data-testid={`vehicle-detail-link-${v.id}`}
                  >
                    <img
                      src={v.imagem}
                      alt={v.nome}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#131620] to-transparent" />
                    {v.destaque && (
                      <span className="absolute top-4 left-4 bg-emerald-500 text-emerald-950 text-xs font-bold px-3 py-1 rounded-full">
                        DESTAQUE
                      </span>
                    )}
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="glass rounded-full px-5 py-2 text-sm font-semibold text-white">Ver detalhes →</span>
                    </span>
                    <span className="absolute bottom-4 right-4 glass rounded-full px-4 py-1.5 font-display font-bold text-emerald-400" data-testid={`vehicle-price-${v.id}`}>
                      {formatBRL(v.preco)}
                    </span>
                  </div>

                  <div className="p-6">
                    <h3
                      className="font-display text-xl font-bold text-white cursor-pointer hover:text-emerald-400 transition-colors"
                      onClick={() => navigate(`/veiculo/${v.id}`)}
                    >
                      {v.nome}
                    </h3>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-emerald-500" />{v.ano}</span>
                      <span className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-emerald-500" />{formatKM(v.km)} km</span>
                      <span className="flex items-center gap-1.5"><Cog className="w-3.5 h-3.5 text-emerald-500" />{v.cambio}</span>
                      <span className="flex items-center gap-1.5"><Fuel className="w-3.5 h-3.5 text-emerald-500" />{v.combustivel}</span>
                    </div>
                    <div className="mt-6 flex gap-3">
                      <button
                        onClick={() => onSimulate(v)}
                        data-testid={`vehicle-simulate-${v.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500/10 hover:bg-emerald-500 text-emerald-400 hover:text-emerald-950 text-sm font-semibold px-4 py-2.5 rounded-full transition-colors duration-300"
                      >
                        <Calculator className="w-4 h-4" />
                        Simular
                      </button>
                      <button
                        onClick={() => onInterest(v)}
                        data-testid={`vehicle-interest-${v.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 glass hover:border-emerald-500/50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors duration-300"
                      >
                        <Heart className="w-4 h-4" />
                        Tenho interesse
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
};
